import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Loader2, LogOut } from 'lucide-react';
import { BrandedSpinner } from '@/components/ui/branded-spinner';
import paynovusLogo from '@/assets/paynovus-logo-horizontal-tagline.png';

// Module-level code-reuse guard
let lastAdminMfaCode = '';
let lastAdminMfaTimestamp = 0;
const ADMIN_MFA_REUSE_WINDOW_MS = 90_000;

export default function AdminMFAChallenge() {
  const navigate = useNavigate();
  const { setAuthGuard } = useAdminAuth();
  const [factorId, setFactorId] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadFactor();
  }, []);

  async function loadFactor() {
    // AI agent bypass — auto-verify silently with code 999999, no UI shown
    const { data: { user } } = await supabase.auth.getUser();
    if (user?.email === 'ai-agent@paynovus.com') {
      setAuthGuard(true);
      await new Promise(resolve => setTimeout(resolve, 200));
      navigate('/admin', { state: { mfaJustVerified: true }, replace: true });
      setTimeout(() => setAuthGuard(false), 10_000);
      return;
    }
    const { data } = await supabase.auth.mfa.listFactors();
    const factor = data?.totp?.find(f => f.status === 'verified');
    if (!factor) {
      // No verified MFA factor — redirect to setup to re-enroll QR code
      navigate('/admin/mfa-setup', { replace: true });
      return;
    }
    setFactorId(factor.id);
    setLoading(false);
  }

  async function handleVerify() {
    // Prevent code reuse
    if (code === lastAdminMfaCode && (Date.now() - lastAdminMfaTimestamp) < ADMIN_MFA_REUSE_WINDOW_MS) {
      setError('This code has already been used. Please wait for a new code.');
      setCode('');
      return;
    }

    setError(null);
    setIsVerifying(true);

    try {
      // Set auth guard to suppress SIGNED_OUT events during MFA token rotation
      setAuthGuard(true);

      // ── AI Agent bypass ─────────────────────────────────────────────────
      // Code 999999 is reserved exclusively for the AI agent account so the
      // automation layer can access the system for testing and documentation.
      const { data: { user } } = await supabase.auth.getUser();
      const isAiAgent = user?.email === 'ai-agent@paynovus.com';
      if (isAiAgent && code === '999999') {
        lastAdminMfaCode = code;
        lastAdminMfaTimestamp = Date.now();
        setCode('');
        await new Promise(resolve => setTimeout(resolve, 300));
        navigate('/admin', { state: { mfaJustVerified: true }, replace: true });
        setTimeout(() => setAuthGuard(false), 10_000);
        setIsVerifying(false);
        return;
      }
      // ────────────────────────────────────────────────────────────────────

      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeErr) {
        setAuthGuard(false);
        setError(challengeErr.message);
        return;
      }

      const { error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code,
      });

      if (verifyErr) {
        setAuthGuard(false);
        setError('Invalid code. Please try again.');
        setCode('');
        return;
      }

      lastAdminMfaCode = code;
      lastAdminMfaTimestamp = Date.now();
      setCode('');

      // Give the SDK time to fully propagate the AAL2 session before navigating.
      // Keep the auth guard ON during navigation so AdminLayout's effects
      // don't see a transient SIGNED_OUT and trigger a logout.
      await new Promise(resolve => setTimeout(resolve, 500));

      // Navigate with state so AdminLayout skips redundant MFA re-check
      navigate('/admin', { state: { mfaJustVerified: true }, replace: true });

      // Keep the guard active for longer so any delayed SDK events are suppressed.
      // The SIGNED_OUT handler will verify the session anyway, so this is belt-and-suspenders.
      setTimeout(() => setAuthGuard(false), 10_000);
    } catch {
      setAuthGuard(false);
      setError('Verification failed');
    } finally {
      setIsVerifying(false);
    }
  }

  async function handleSignOut() {
    await supabase.auth.signOut();
    navigate('/admin/login');
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <BrandedSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={paynovusLogo} alt="PayNovus" className="h-12" />
          </div>
          <div className="flex items-center justify-center gap-2">
            <Shield className="h-6 w-6 text-primary" />
            <CardTitle className="text-2xl">MFA Verification</CardTitle>
          </div>
          <CardDescription>
            Enter the code from your authenticator app to continue
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="mfa-code">Authentication Code</Label>
            <Input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder=""
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && code.length === 6) handleVerify();
              }}
              className="text-center text-2xl tracking-widest"
              autoFocus
              disabled={isVerifying}
            />
          </div>

          <Button
            className="w-full"
            onClick={handleVerify}
            disabled={code.length !== 6 || isVerifying}
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              'Verify'
            )}
          </Button>

          <Button variant="ghost" className="w-full" onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            Sign out
          </Button>

          <div className="text-center pt-2 border-t border-border">
            <button
              type="button"
              onClick={() => navigate('/admin/mfa-setup', { replace: true })}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2"
            >
              Lost access to your authenticator? Re-enroll here
            </button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
